import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  OPERATOR_SPECIALTY,
  FILING_STATUS,
  ONBOARDING_STATUS,
  OnboardingSessionStepData,
  BookingInfoCal,
} from '@shared-types/db-types';
import {
  submitOnboardingStep,
  postToOnboarding,
  fetchOnboardingData,
} from '@services/onboarding-api';
import { onboardingAnalytics } from '@services/onboarding-analytics';
import {
  ONBOARDING_STEPS,
  calculateTotalOnboardingSteps,
} from '@constants/onboarding';
import { OnboardingStep } from '@constants/onboardingSteps';

export const INITIAL_ANNUAL_INCOME = 50000;
export const INITIAL_LIQUID_NET_WORTH = INITIAL_ANNUAL_INCOME;

const defaultState: OnboardingSessionStepData = {
  step: OnboardingStep.ANNUAL_INCOME,
  birthday: '',
  annualIncome: INITIAL_ANNUAL_INCOME,
  liquidNetWorth: INITIAL_LIQUID_NET_WORTH,
  country: 'US', // Default to US
  taxState: '',
  filingStatus: FILING_STATUS.SINGLE,
  riskPreference: OPERATOR_SPECIALTY.DEDUCTIONS,
  incomeType: null,
  riskTolerance: null,
  accreditationMethod: null,
  interests: [],
  bookingInfo: undefined,
};

// Separate user state for user fields that are no longer in stepData
interface UserState {
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  status: ONBOARDING_STATUS | null;
}

const defaultUserState: UserState = {
  email: '',
  firstName: '',
  lastName: '',
  phone: '',
  status: null,
};

// Cache for the calculated total steps
let cachedTotalSteps: number | null = null;

interface OnboardingStore extends OnboardingSessionStepData, UserState {
  setStep: (step: number, totalSteps?: number) => void;
  nextStep: (totalSteps?: number) => void;
  prevStep: () => void;
  setBirthday: (value: string) => void;
  setAnnualIncome: (value: number) => void;
  setLiquidNetWorth: (value: number) => void;
  setCountry: (value: string) => void;
  setTaxState: (value: string) => void;
  setFilingStatus: (value: FILING_STATUS) => void;
  setRiskPreference: (
    value: OnboardingSessionStepData['riskPreference']
  ) => void;
  setAccreditationMethod: (
    value: OnboardingSessionStepData['accreditationMethod']
  ) => void;
  setEmail: (value: string) => void;
  setFirstName: (value: string) => void;
  setLastName: (value: string) => void;
  setFullName: (value: string) => void; // Helper for backward compatibility
  getFullName: () => string; // Helper to compute fullName from firstName + lastName
  setPhone: (value: string) => void;
  setInterests: (value: string[]) => void;
  setBookingInfo: (value: BookingInfoCal) => void;
  setStatus: (value: ONBOARDING_STATUS | null) => void;
  estimateTaxSavings: () => number;
  getTotalSteps: () => number;
  recalculateTotalSteps: () => number;
  hydrateFromDatabase: () => Promise<void>;
  clearAllData: () => void;
  hasResumableData: () => boolean;
  getLastCompletedStep: () => OnboardingStep;
}

const useOnboardingStore = create<OnboardingStore>()(
  persist(
    (set, get) => ({
      ...defaultState,
      ...defaultUserState,
      getTotalSteps: () => {
        if (cachedTotalSteps === null) {
          const currentState = get();
          cachedTotalSteps = calculateTotalOnboardingSteps({
            country: currentState.country,
          });
        }
        return cachedTotalSteps;
      },
      recalculateTotalSteps: () => {
        const currentState = get();
        cachedTotalSteps = calculateTotalOnboardingSteps({
          country: currentState.country,
        });
        return cachedTotalSteps;
      },
      setStep: (step: number, totalSteps?: number) => {
        set(state => {
          const actualTotalSteps =
            totalSteps ||
            calculateTotalOnboardingSteps({ country: state.country });
          const validStep = Math.max(
            OnboardingStep.ANNUAL_INCOME,
            Math.min(step, actualTotalSteps)
          );

          // Reset cache when country changes
          if (totalSteps) {
            cachedTotalSteps = totalSteps;
          }

          return { step: validStep };
        });
      },
      nextStep: totalSteps => {
        const currentState = get();
        const actualTotalSteps = totalSteps ?? get().getTotalSteps();
        const nextStep = Math.min(currentState.step + 1, actualTotalSteps);

        // Submit the current step's data before moving to the next step
        const getStepData = () => {
          switch (currentState.step) {
            case OnboardingStep.ANNUAL_INCOME:
              return { annualIncome: currentState.annualIncome };
            case OnboardingStep.LIQUID_NET_WORTH:
              return { liquidNetWorth: currentState.liquidNetWorth };
            case OnboardingStep.TAX_FILING_LOCATION:
              return {
                country: currentState.country,
                taxState: currentState.taxState,
              };
            case OnboardingStep.FILING_STATUS_OR_REQUEST_ACCESS:
              // Check if this is non-US flow (4 steps total)
              if (actualTotalSteps === 4) {
                // Non-US flow - RequestAccessStep (final step)
                // User fields now go in user object, not stepData
                return {
                  user: {
                    email: currentState.email,
                    firstName: currentState.firstName,
                    lastName: currentState.lastName,
                  },
                  stepData: {
                    interests: currentState.interests,
                  },
                };
              } else {
                // US flow - FilingStatusStep
                return { filingStatus: currentState.filingStatus };
              }
            case OnboardingStep.RISK_PREFERENCE:
              // Only US flow reaches step 6
              // US flow - RiskPreferenceStep
              return { riskPreference: currentState.riskPreference };
            case OnboardingStep.ACCREDITATION:
              // AccreditationStep - submit accreditation method
              return { accreditationMethod: currentState.accreditationMethod };
            case OnboardingStep.LEAD_CAPTURE:
              // LeadCaptureStep - submit lead capture data
              // User fields now go in user object, not stepData
              return {
                user: {
                  email: currentState.email,
                  firstName: currentState.firstName,
                  lastName: currentState.lastName,
                  phone: currentState.phone,
                },
              };
            // case OnboardingStep.CELEBRATION:
            //   // Only in full flow (final step)
            //   return actualTotalSteps > OnboardingStep.LEAD_CAPTURE
            //     ? {
            //         email: currentState.email,
            //         fullName: currentState.fullName,
            //         phone: currentState.phone,
            //       }
            //     : null;
            default:
              return null;
          }
        };

        const stepData = getStepData();

        if (stepData) {
          const stepName =
            ONBOARDING_STEPS[
              currentState.step as keyof typeof ONBOARDING_STEPS
            ];

          // Always include user data if available
          const userInfo = {
            email: currentState.email,
            firstName: currentState.firstName,
            lastName: currentState.lastName,
            phone: currentState.phone,
          };

          // Only include user data if at least one field has a value
          const hasUserData = Object.values(userInfo).some(
            value => value && value.trim() !== ''
          );

          let finalStepData: Record<string, unknown> = stepData;

          // Always include user data if available, either by adding it or merging with existing user object
          if (hasUserData) {
            if ('user' in stepData && stepData.user) {
              // Merge with existing user object, ensuring we have the latest data
              finalStepData = {
                ...stepData,
                user: {
                  ...(stepData.user as Record<string, unknown>),
                  ...userInfo,
                },
              };
            } else {
              // Add user data if not present
              finalStepData = {
                ...stepData,
                user: userInfo,
              };
            }
          }

          // Submit to API (analytics tracking is handled inside submitOnboardingStep)
          submitOnboardingStep({
            stepNumber: currentState.step,
            stepName,
            stepData: finalStepData,
            totalSteps: actualTotalSteps,
          });
        }

        // Track completion of entire onboarding flow
        if (currentState.step === actualTotalSteps) {
          onboardingAnalytics.trackCompletion(actualTotalSteps, {
            birthday: currentState.birthday,
            annualIncome: currentState.annualIncome,
            liquidNetWorth: currentState.liquidNetWorth,
            country: currentState.country,
            taxState: currentState.taxState,
            filingStatus: currentState.filingStatus,
            riskPreference: currentState.riskPreference,
            accreditationMethod: currentState.accreditationMethod,
            email: currentState.email,
            firstName: currentState.firstName,
            lastName: currentState.lastName,
            phone: currentState.phone,
            estimatedTaxSavings: get().estimateTaxSavings(),
          });
        }

        set({ step: nextStep });
      },
      prevStep: () => {
        const currentState = get();
        const prevStep = Math.max(
          currentState.step - 1,
          OnboardingStep.ANNUAL_INCOME
        );

        // Track back navigation
        if (prevStep !== currentState.step) {
          onboardingAnalytics.trackBackNavigation(currentState.step, prevStep);
        }

        set({ step: prevStep });
      },
      setBirthday: birthday => set({ birthday }),
      setAnnualIncome: annualIncome => set({ annualIncome }),
      setLiquidNetWorth: liquidNetWorth => set({ liquidNetWorth }),
      setCountry: country => {
        // Invalidate cached total steps when country changes
        cachedTotalSteps = null;
        set({ country });
      },
      setTaxState: taxState => set({ taxState }),
      setFilingStatus: filingStatus => set({ filingStatus }),
      setRiskPreference: riskPreference => set({ riskPreference }),
      setAccreditationMethod: accreditationMethod =>
        set({ accreditationMethod }),
      setEmail: email => set({ email }),
      setFirstName: firstName => set({ firstName }),
      setLastName: lastName => set({ lastName }),
      setFullName: (fullName: string) => {
        // Handle trailing spaces by checking if input ends with space
        const endsWithSpace = fullName.endsWith(' ');
        const parts = fullName.split(' ');
        const firstName = parts[0] || '';
        let lastName = parts.slice(1).join(' ');

        // If input ended with space but lastName is empty, preserve the space
        if (endsWithSpace && lastName === '' && firstName !== '') {
          lastName = ' '; // Store the space as lastName
        }

        set({ firstName, lastName });
      },
      getFullName: () => {
        const state = get();

        // If lastName is just a space (trailing space case), return firstName + space
        if (state.firstName && state.lastName === ' ') {
          return `${state.firstName} `;
        }

        // Only add space if both firstName and lastName have actual content
        if (state.firstName && state.lastName && state.lastName !== ' ') {
          return `${state.firstName} ${state.lastName}`;
        }

        // If only firstName or only lastName, return just that part
        return (
          state.firstName ||
          (state.lastName === ' ' ? '' : state.lastName) ||
          ''
        );
      },
      setPhone: phone => set({ phone }),
      setInterests: interests => set({ interests }),
      setStatus: status => set({ status }),
      setBookingInfo: bookingInfo => {
        set({ bookingInfo });

        // Also post booking info to database
        postToOnboarding(
          { stepData: { bookingInfo } },
          'Save booking information',
          {
            context: 'booking_info_save',
          }
        ).catch((error: unknown) => {
          console.warn('Failed to save booking info to database:', error);
        });
      },

      hydrateFromDatabase: async () => {
        try {
          const data = await fetchOnboardingData();

          if (data) {
            // Merge database data with current state, preserving any newer local data
            const currentState = get();
            const mergedState = {
              ...currentState,
              ...data,
              // Preserve local step if it's higher than database step
              step: Math.max(
                currentState.step,
                data.step || OnboardingStep.ANNUAL_INCOME
              ),
            };

            set(mergedState);
          }
        } catch (error) {
          console.error('Failed to hydrate onboarding data:', error);
        }
      },

      clearAllData: () => {
        set({ ...defaultState, ...defaultUserState });
      },

      hasResumableData: () => {
        const state = get();

        // Check if we have meaningful data beyond the default state
        // Note: We check against default values, not just > 0 since defaults are 50000
        const hasData =
          state.birthday !== '' ||
          state.annualIncome !== defaultState.annualIncome ||
          state.liquidNetWorth !== defaultState.liquidNetWorth ||
          state.country !== defaultState.country ||
          state.taxState !== '' ||
          state.filingStatus !== defaultState.filingStatus ||
          state.riskPreference !== defaultState.riskPreference ||
          state.email !== '' ||
          state.firstName !== '' ||
          state.lastName !== '' ||
          state.phone !== '' ||
          state.accreditationMethod !== null ||
          state.bookingInfo !== undefined ||
          state.step > OnboardingStep.ANNUAL_INCOME;

        return hasData;
      },

      getLastCompletedStep: () => {
        const state = get();
        let lastStep = 0; // Start at 0 for no completed steps

        // Birthday step is 0 but not in enum anymore
        if (state.birthday) lastStep = 0;
        if (state.annualIncome > INITIAL_ANNUAL_INCOME)
          lastStep = OnboardingStep.ANNUAL_INCOME;
        if (state.liquidNetWorth > INITIAL_LIQUID_NET_WORTH)
          lastStep = OnboardingStep.LIQUID_NET_WORTH;
        if (state.country && state.taxState)
          lastStep = OnboardingStep.TAX_FILING_LOCATION;
        if (state.filingStatus !== FILING_STATUS.SINGLE)
          lastStep = OnboardingStep.FILING_STATUS_OR_REQUEST_ACCESS;
        if (state.riskPreference !== OPERATOR_SPECIALTY.DEDUCTIONS)
          lastStep = OnboardingStep.RISK_PREFERENCE;
        if (state.accreditationMethod) lastStep = OnboardingStep.ACCREDITATION;
        // Handle lead capture completion
        if (state.email && state.firstName && state.lastName && state.phone) {
          const completedStatuses = [
            ONBOARDING_STATUS.ESTIMATE_VIEWED,
            ONBOARDING_STATUS.CALL_SCHEDULED,
            ONBOARDING_STATUS.CALL_ATTENDED,
            ONBOARDING_STATUS.NOT_INVESTING,
            ONBOARDING_STATUS.CONVERTED,
            ONBOARDING_STATUS.NOT_VALID,
          ];

          // If they've actually finished onboarding (have completed status), they completed LEAD_CAPTURE
          if (state.status && completedStatuses.includes(state.status)) {
            lastStep = OnboardingStep.LEAD_CAPTURE;
          } else {
            // If they filled the form but haven't submitted it yet, they're still on LEAD_CAPTURE
            // but it's not completed, so last completed step should be ACCREDITATION
            lastStep = Math.max(lastStep, OnboardingStep.ACCREDITATION);
          }
        }

        return lastStep;
      },

      estimateTaxSavings: () => {
        const { annualIncome, filingStatus, riskPreference } = get();

        // Validate inputs
        if (!annualIncome || annualIncome <= 0) {
          return 0;
        }

        const baseRate =
          filingStatus === FILING_STATUS.MARRIED_JOINT ? 0.17 : 0.15;
        const riskMultiplier =
          riskPreference === OPERATOR_SPECIALTY.DEDUCTIONS ? 1.2 : 1;

        const estimatedSavings = Math.round(
          annualIncome * baseRate * riskMultiplier
        );

        // Track tax savings calculation
        onboardingAnalytics.trackTaxSavingsCalculation(
          annualIncome,
          estimatedSavings,
          filingStatus,
          get().taxState || 'unknown'
        );

        return estimatedSavings;
      },
    }),
    {
      name: 'fieldvest-onboarding',
      partialize: state => ({
        // Persist all onboarding data
        step: state.step,
        birthday: state.birthday,
        annualIncome: state.annualIncome,
        liquidNetWorth: state.liquidNetWorth,
        country: state.country,
        taxState: state.taxState,
        filingStatus: state.filingStatus,
        riskPreference: state.riskPreference,
        accreditationMethod: state.accreditationMethod,
        email: state.email,
        firstName: state.firstName,
        lastName: state.lastName,
        phone: state.phone,
        interests: state.interests,
        bookingInfo: state.bookingInfo,
        status: state.status,
      }),
    }
  )
);

export default useOnboardingStore;
